import { useState, useEffect } from 'react';
import { Download, Share, Chrome, AlertTriangle, ExternalLink, Copy, Check, MoreVertical, Smartphone, PlusSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { triggerNativeInstall, waitForInstallPrompt, openInChrome, copyCurrentLink, trackInstall } from '@/lib/installContext';

const Steps = ({ steps, big = false }) => (
  <ol className={`mt-3 bg-[#0A0A0A] rounded-lg p-3 ${big ? 'space-y-3' : 'space-y-2'}`} data-testid="install-manual-steps">
    {steps.map((s, i) => (
      <li key={i} className={`flex items-start gap-2 ${big ? 'text-sm' : 'text-xs'} text-[#A1A1AA]`}>
        <span className={`text-[#FFD700] font-bold flex-shrink-0 ${big ? 'w-6 h-6 rounded-full bg-[#FFD700]/15 flex items-center justify-center text-xs' : ''}`}>{big ? i + 1 : `${i + 1}.`}</span>
        <span className="pt-0.5">{s}</span>
      </li>
    ))}
  </ol>
);

const Dots = () => <MoreVertical className="w-3.5 h-3.5 inline text-white align-text-bottom" />;

function OpenInChromeButton({ label = 'Ouvrir dans Chrome' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    trackInstall('copy_link_click');
    const ok = await copyCurrentLink();
    setCopied(ok);
    toast[ok ? 'success' : 'error'](ok ? 'Lien copié — collez-le dans Chrome' : 'Copie impossible');
  };
  return (
    <div className="mt-3 flex gap-2">
      <button onClick={() => openInChrome()} data-testid="install-open-chrome-btn"
        className="flex-1 py-3 rounded-xl bg-[#FFD700] text-black text-sm font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
        <Chrome className="w-4 h-4" /> {label}
      </button>
      <button onClick={copy} data-testid="install-copy-link-btn" title="Copier le lien"
        className="px-3 rounded-xl bg-[#27272A] text-white flex items-center justify-center active:scale-95 transition-transform">
        {copied ? <Check className="w-4 h-4 text-[#22C55E]" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

// Big "Installer" button for Android: native prompt when Chrome allows it, otherwise a guided dialog (menu ⋮ → Installer l'application).
function AndroidInstallButton({ ctx, onInstalled }) {
  const [busy, setBusy] = useState(false);
  const [guide, setGuide] = useState(false);

  const install = async () => {
    setBusy(true);
    const ready = await waitForInstallPrompt(ctx.canPrompt ? 0 : 2500);
    if (ready) {
      const outcome = await triggerNativeInstall();
      setBusy(false);
      if (outcome === 'accepted') { toast.success('MboaSend installée — retrouvez l’icône sur votre écran d’accueil'); onInstalled?.(); }
      return;
    }
    setBusy(false);
    trackInstall('manual_steps_shown', ctx);
    setGuide(true);
  };

  return (
    <>
      <button onClick={install} disabled={busy} data-testid="install-native-btn"
        className="mt-3 w-full py-3 rounded-xl bg-[#FFD700] text-black text-sm font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
        <Download className="w-4 h-4" /> {busy ? 'Préparation…' : 'Installer MboaSend'}
      </button>
      <Dialog open={guide} onOpenChange={setGuide}>
        <DialogContent className="bg-[#18181B] border-[#FFD700]/30 text-white max-w-sm" data-testid="install-guide-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white"><Smartphone className="w-5 h-5 text-[#FFD700]" /> Installer en 3 étapes</DialogTitle>
            <DialogDescription className="text-[#A1A1AA] text-xs">{ctx.browserLabel} n’a pas proposé l’installation automatique. Elle prend 10 secondes manuellement :</DialogDescription>
          </DialogHeader>
          <Steps big steps={[
            <>Touchez le menu <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-[#27272A] align-middle"><Dots /></span> en haut à droite de {ctx.browserLabel}</>,
            <>Choisissez <b className="text-white">« Installer l’application »</b> ou <b className="text-white">« Ajouter à l’écran d’accueil »</b> <PlusSquare className="w-3.5 h-3.5 inline text-[#FFD700]" /></>,
            <>Confirmez avec <b className="text-white">« Installer »</b> — l’icône MboaSend apparaît sur votre écran d’accueil</>,
          ]} />
          <p className="text-[11px] text-[#71717A]">Si le menu affiche « Ouvrir dans Chrome », touchez-le d’abord puis recommencez depuis Chrome.</p>
          {ctx.browser !== 'chrome' && <OpenInChromeButton />}
          <button onClick={() => setGuide(false)} className="w-full py-2.5 rounded-xl bg-[#27272A] text-white text-sm" data-testid="install-guide-dialog-close">J’ai compris</button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function InstallInstructions({ ctx, onInstalled, compact = false }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (ctx.standalone || !ctx.isAndroid) return;
    if (ctx.browser === 'inapp') trackInstall('inapp_shown', ctx);
    else if (ctx.browser === 'samsung') trackInstall('samsung_block_shown', ctx);
  }, [ctx.browser, ctx.standalone, ctx.isAndroid]); // eslint-disable-line react-hooks/exhaustive-deps

  const native = async () => {
    setBusy(true);
    const outcome = await triggerNativeInstall();
    setBusy(false);
    if (outcome === 'accepted') { toast.success('MboaSend installée — retrouvez l’icône sur votre écran d’accueil'); onInstalled?.(); }
    else if (outcome === 'unavailable') toast.error('Installation indisponible — utilisez l’icône d’installation de la barre d’adresse');
  };

  if (ctx.standalone) {
    return <p className="text-xs text-[#22C55E] flex items-center gap-2" data-testid="install-already"><Check className="w-4 h-4" /> MboaSend est déjà installée et ouverte en mode application.</p>;
  }

  // iOS — Safari required
  if (ctx.isIOS) {
    if (ctx.browser !== 'safari') {
      return (
        <div data-testid="install-ios-not-safari">
          <p className="text-xs text-[#A1A1AA]">Sur iPhone, l’installation se fait uniquement depuis <b className="text-white">Safari</b>.</p>
          <Steps steps={[<>Touchez <Dots /> ou le bouton de partage dans {ctx.inAppName || ctx.browserLabel}</>, 'Choisissez « Ouvrir dans Safari »', <>Dans Safari : <Share className="w-3.5 h-3.5 text-[#3B82F6] inline" /> → « Sur l’écran d’accueil » → « Ajouter »</>]} />
        </div>
      );
    }
    return (
      <div data-testid="install-ios-safari">
        <p className="text-xs text-[#A1A1AA]">Accédez plus rapidement depuis votre écran d’accueil.</p>
        <Steps steps={[<>Appuyez sur <Share className="w-3.5 h-3.5 text-[#3B82F6] inline" /> en bas de Safari</>, 'Sélectionnez « Sur l’écran d’accueil »', 'Appuyez sur « Ajouter »']} />
      </div>
    );
  }

  // Android — in-app browser or Chrome Custom Tab (WhatsApp, Facebook…)
  if (ctx.browser === 'inapp') {
    return (
      <div data-testid="install-inapp">
        <p className="text-xs text-[#A1A1AA]">Vous avez ouvert ce lien depuis <b className="text-white">{ctx.inAppName}</b>, qui ne permet pas d’installer des applications. Ouvrez <b className="text-white">mboasend.com</b> dans Chrome.</p>
        <OpenInChromeButton />
        {!compact && <Steps steps={[<>Si rien ne se passe : touchez <Dots /> en haut à droite</>, 'Choisissez « Ouvrir dans Chrome » ou « Ouvrir dans le navigateur »', 'Dans Chrome, touchez « Installer MboaSend »']} />}
      </div>
    );
  }

  // Android — Samsung Internet (Play Protect block)
  if (ctx.browser === 'samsung') {
    return (
      <div data-testid="install-samsung">
        <div className="flex items-start gap-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-2.5">
          <AlertTriangle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#FCD34D]">Samsung Internet peut bloquer l’installation (message Google Play Protect). Ne désactivez pas Play Protect : installez simplement depuis <b>Chrome</b>.</p>
        </div>
        <OpenInChromeButton />
        {!compact && <Steps steps={['Chrome s’ouvre sur mboasend.com', 'Touchez « Installer MboaSend »', <>Ou menu <Dots /> → « Installer l’application »</>]} />}
      </div>
    );
  }

  // Android — Chrome / Edge / other : always a direct install button
  if (ctx.isAndroid) {
    return (
      <div data-testid={ctx.browser === 'chrome' || ctx.browser === 'edge' ? 'install-chrome' : 'install-other-android'}>
        <p className="text-xs text-[#A1A1AA]">Ajoutez l’app à votre écran d’accueil : plus rapide, plein écran, toujours à portée de main.</p>
        <AndroidInstallButton ctx={ctx} onInstalled={onInstalled} />
        {ctx.browser !== 'chrome' && ctx.browser !== 'edge' && <p className="mt-2 text-[11px] text-[#71717A]">Navigateur recommandé : Chrome. <button onClick={() => openInChrome()} className="text-[#FFD700] underline underline-offset-2" data-testid="install-open-chrome-link">Ouvrir dans Chrome</button></p>}
      </div>
    );
  }

  // Desktop
  return (
    <div data-testid="install-desktop">
      <p className="text-xs text-[#A1A1AA]">Sur ordinateur, utilisez l’icône d’installation <ExternalLink className="w-3.5 h-3.5 inline" /> dans la barre d’adresse de Chrome ou Edge.</p>
      {ctx.canPrompt && (
        <button onClick={native} disabled={busy} data-testid="install-native-btn"
          className="mt-3 w-full py-2.5 rounded-xl bg-[#FFD700] text-black text-sm font-semibold flex items-center justify-center gap-2">
          <Download className="w-4 h-4" /> Installer MboaSend
        </button>
      )}
    </div>
  );
}
